import ProfileData from '../data/profile.js';
import styled from 'styled-components';
import BackHeader from '../shared/components/BackHeader';
import {bgBody, gray200, gray500, gray600, gray900} from '../shared/themes/color';
import ContentWrapper from '../shared/components/ContentWrapper';
import {useState, useRef} from 'react';
import useOutsideClick from '../hooks/useOutsideClick';

const dataNavProfile = ProfileData;
const dataAbstract = ProfileData[0];
const dataPersonal = ProfileData[1].content;
const dataFormative = ProfileData[2].content;
const dataExperience = ProfileData[3].content;
const dataOccupational = ProfileData[4].content;
const dataProjects = ProfileData[5].content;
const dataTraining = ProfileData[6].content;
const dataCertificate = ProfileData[7].content;
const dataMethodology = ProfileData[8].content;
const dataSkills = ProfileData[9].content;
const dataTools = ProfileData[10].content;
const dataLanguage = ProfileData[11].content;

const DataContainer = styled.main`
  display: grid;
  grid-template: auto / 8fr 4fr;
  gap: 1.25rem;

  @media (max-width: 600px) {
    display: block;
  }
`;

const AbstractContainer = styled.div`
  display: grid;
  grid-template: auto / 8fr 4fr;
  gap: 1.25rem;

  @media (max-width: 600px) {
    display: block;
  }
`;

const ProfilesContainer = styled.div`
  padding-bottom: 2.5rem;
  border-bottom: 1px solid ${gray200};

  h3 {
    margin-bottom: .5rem;
  }

  p {
    margin-top: 1.75rem;
    font-size: 1.13rem;
    line-height: 1.4;
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  transition: all 200ms ease-in-out;

  @media (max-width: 600px) {
    display: ${props => props.floatActions ? 'flex' : 'none'};
    position: fixed;
    top: 4rem;
    right: 3.5rem;
    padding: 0 .25rem;
    border-radius: .25rem;
    background: ${bgBody};
    box-shadow: -1px 3px 8px rgba(0, 0, 0, .075);
  }

  i {
    margin-left: .5rem;
    padding: .5rem;
    cursor: pointer;
    color: ${gray900};

    @media (max-width: 600px) {
      display: block;
      margin: 0 .25rem;
    }

    @media print {
      display: none;
    }
  }
`;

const LevelLabel = styled.span`
  margin-left: .5rem;
  padding: .25rem .5rem;
  font-size: .81rem;
  font-weight: 500;
  line-height: 1rem;
  color: ${bgBody};
  background: ${gray900};
`;

const DataSection = styled.section`
  padding-bottom: 1rem;
  border-bottom: ${props => props.separator ? '1px solid' : 'none'} ${gray200};

  :before {
    content: '';
    display: block;
    height: 2.5rem;
  }

  > p {
    font-size: 1.13rem;
    line-height: 1.4;
  }

  @media print {
    page-break-inside: avoid;
  }
`;

const ItemsContainer = styled.div`
  margin: 2.5rem 0 2rem 0;
`;

const DataItem = styled.div`
  margin-bottom: 2rem;

  h3 {
    margin-right: .25rem;
  }
`;

const LabelType = styled.span`
  display: inline-block;
  margin-left: 0;
  margin-bottom: .2rem;
  padding: .25rem .35rem;
  vertical-align: middle;
  font-size: .62rem;
  font-weight: 600;
  letter-spacing: .5px;
  text-transform: uppercase;
  line-height: 1;
  color: ${bgBody};
  background: ${gray500};
`;

const ItemAgency = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: .25rem;

  a {
    display: ${props => props.showLink ? 'block' : 'none'};
    font-size: 1.25rem;
    line-height: 1;
    margin: 0 0 0 .5rem;
    text-decoration: none;

    :visited {
      color: ${gray500};
    }

    i {
      display: block;
      font-size: inherit;
      line-height: inherit;
      color: ${gray500};

      :hover {
        color: ${gray900};
      }
    }
  }
`;

const DataList = styled.div`

  p {
    margin-bottom: .75rem;
    display: flex;
    font-weight: 500;

    :before {
      content: '${props => props.bullet || 'chevron_right'}';
      font-family: "Material Icons", sans-serif;
      font-size: 1.25rem;
      margin-right: .5rem;
      color: ${gray500};
    }
  }
`;

const CertificateContainer = styled.div`
  margin-bottom: 2rem;

  p:nth-child(odd) {
    display: flex;
    font-weight: 500;

    :before {
      content: '${props => props.bullet || 'chevron_right'}';
      font-family: "Material Icons", sans-serif;
      font-size: 1.25rem;
      margin-right: .5rem;
      color: ${gray500};
    }
  }

  p:nth-child(even) {
    margin-left: 1.75rem;
  }

`;

const ActivityContainer = styled.div`
  margin-top: .5rem;

  ul {
    margin: 0;
    padding: 0;
    color: ${gray600};
    font-size: .87rem;

    li {
      position: relative;
      margin: 0 0 .25rem 0;
      list-style: none;
      line-height: 1.25rem;
      padding-left: 1.5rem;

      :before {
        content: '•';
        position: absolute;
        left: .25rem;
      }
    }
`;

const SkillsContainer = styled.div`
  div {

    p {
      margin: .75rem 0 ${props => props.displaySub ? '0' : '.75rem'} 0;
      display: flex;
      font-weight: 500;

      :before {
        content: '${props => props.bullet || 'chevron_right'}';
        font-family: "Material Icons", sans-serif;
        font-size: 1.25rem;
        margin-right: .5rem;
        color: ${gray500};
      }
    }

    div {
      display: ${props => props.displaySub ? 'block' : 'none'};
      margin-left: 1.75rem;

      span {
        font-size: .81rem;
        font-weight: normal;
        color: ${gray600};

        :after {
          content: '/';
          margin: 0 .25rem;
        }
      }

      span:last-child {

        :after {
          content: none;
        }
      }
    }
  }
`;

const Label = styled.span`
  display: block;
  margin: 0 0 .25rem 0;
  font-style: normal;
  font-weight: 500;
  font-size: 11px;
  line-height: .75rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${gray500};
`;

const NavRightContainer = styled.div`
  position: sticky;
  position: -webkit-sticky;
  top: 2.5rem;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 600px) {
    display: ${props => props.floatSections ? 'block' : 'none'};
    position: fixed;
    top: 7.5rem;
    right: 3.5rem;
    padding: 0 1rem;
    border-radius: .25rem;
    background: ${bgBody};
    box-shadow: -1px 3px 8px rgba(0, 0, 0, .075);
  }

  @media print {
    display: none;
  }

  div {
  }
`;

const NavItem = styled.a`
  display: block;
  margin: 0 0 .75rem 0;
  font-style: normal;
  font-weight: ${props => props.active ? '500' : 'normal'};
  font-size: .81rem;
  line-height: 1rem;
  text-decoration: none;
  color: ${props => props.active ? gray900 : gray600};
  transition: all 200ms ease-in-out;

  :hover {
    color: ${gray900};
  }
`;

const MoreMenuIcon = styled.i`
  display: none;
  position: fixed;
  top: 4rem;
  right: .5rem;
  padding: .5rem;
  cursor: pointer;
  color: ${gray500};

  :hover {
    color: ${gray900};
  }

  @media (max-width: 600px) {
    display: block;
  }

  @media print {
    display: none;
  }
`;

const ReturnUpIcon = styled.i`
  position: fixed;
  bottom: 1rem;
  right: 16rem;
  padding: .5rem;
  cursor: pointer;
  color: ${gray500};

  :hover {
    color: ${gray900};
  }

  @media (max-width: 800px) {
    bottom: 1rem;
    right: 10rem;
  }
  
  @media (max-width: 600px) {
    right: .5rem;
    bottom: 2.5rem;
  }

  @media print {
    display: none;
  }
`;

const NavSectionsIcon = styled.i`
  display: none;
  position: fixed;
  top: 7rem;
  right: .5rem;
  padding: .5rem;
  cursor: pointer;
  color: ${gray500};

  :hover {
    color: ${gray900};
  }

  @media (max-width: 600px) {
    display: block;
  }

  @media print {
    display: none;
  }
`;

const Profile = props => {

  const [showFloatActions, setShowFloatActions] = useState(false);
  const [showFloatSections, setShowFloatSections] = useState(false);
  const ref = useRef();

  useOutsideClick(ref, () => {
    if (showFloatActions) setShowFloatActions(false);
    if (showFloatSections) setShowFloatSections(false);
  });

  return (
    <ContentWrapper>
      <BackHeader/>
      <h1>Perfil Profesional</h1>
      <MoreMenuIcon className="material-icons"
                    onClick={() => setShowFloatActions(!showFloatActions)}>{showFloatActions ? 'close' : 'more_horiz'}
      </MoreMenuIcon>
      <ReturnUpIcon className="material-icons"
                    onClick={() => window.scrollTo(0, 0)}>north
      </ReturnUpIcon>
      <AbstractContainer>
        <ProfilesContainer>
          {
            dataAbstract.profiles.map((item, i) => {
              return (
                <div key={i}>
                  <h3>{item.area}</h3><LevelLabel>{item.level}</LevelLabel>
                </div>
              );
            })
          }
          <p dangerouslySetInnerHTML={{__html: dataAbstract.intro}}/>
        </ProfilesContainer>
        <ActionsContainer ref={ref} floatActions={showFloatActions}>
          <i className="material-icons">file_download</i>
          <i className="material-icons" onClick={() => window.print()}>print</i>
        </ActionsContainer>
      </AbstractContainer>

      {/* All sections of data */}
      <DataContainer>
        <div>
          <DataSection separator={props.separator | true} id={ProfileData[1].id}>
            <h2>{ProfileData[1].section}</h2>
            {
              dataPersonal.map((item, i) => {
                return (
                  <DataItem key={i}>
                    <Label>{item.label}</Label>
                    <p>{item.value}</p>
                  </DataItem>
                );
              })
            }
          </DataSection>
          <DataSection separator={props.separator | true} id={ProfileData[2].id}>
            <h2>{ProfileData[2].section}</h2>
            <ItemsContainer>
              {
                dataFormative.map((item, i) => {
                  return (
                    <DataItem key={i}>
                      <Label>{item.degree}</Label>
                      <h3>{item.profession}</h3>
                      <p>{item.institute}</p>
                    </DataItem>
                  );
                })
              }
            </ItemsContainer>
          </DataSection>
          <DataSection separator={props.separator | true} id={ProfileData[3].id}>
            <h2>{ProfileData[3].section}</h2>
            <p dangerouslySetInnerHTML={{__html: ProfileData[3].intro}}/>
            <ItemsContainer>
              <DataList bullet={props.bullet}>
                {
                  dataExperience.map((item, i) => {
                    return <p key={i}>{item}</p>;
                  })
                }
              </DataList>
            </ItemsContainer>
          </DataSection>
          <DataSection separator={props.separator | true} id={ProfileData[4].id}>
            <h2>{ProfileData[4].section}</h2>
            <p>{ProfileData[4].intro}</p>
            <ItemsContainer>
              {
                dataOccupational.map((item, i) => {
                  return (
                    <DataItem key={i}>
                      <Label>{item.date}</Label>
                      <ItemAgency showLink={props.showLink | item.link.show}>
                        <h3>{item.agency}</h3>
                        <a href={item.link.url} target="_blank" rel="noreferrer">
                          <i className="material-icons">link</i>
                        </a>
                      </ItemAgency>
                      <p>{item.rol}</p>
                    </DataItem>
                  );
                })
              }
            </ItemsContainer>
          </DataSection>
          <DataSection separator={props.separator | true} id={ProfileData[5].id}>
            <h2>{ProfileData[5].section}</h2>
            <p>{ProfileData[5].intro}</p>
            <ItemsContainer>
              {
                dataProjects.map((item, i) => {
                  return (
                    <DataItem key={i}>
                      <Label>{item.agency}</Label>
                      <h3>{item.project}</h3><LabelType>{item.type}</LabelType>
                      <p>{item.intro}</p>
                      <ActivityContainer>
                        {
                          item.activity.map((item, i) => {
                            return (
                              <ul key={i}>
                                <li>{item}</li>
                              </ul>
                            );
                          })
                        }
                      </ActivityContainer>
                    </DataItem>
                  );
                })
              }
            </ItemsContainer>
          </DataSection>
          <DataSection separator={props.separator | true} id={ProfileData[6].id}>
            <h2>{ProfileData[6].section}</h2>
            <p>{ProfileData[6].intro}</p>
            <ItemsContainer>
              <DataList bullet={props.bullet || 'moving'}>
                {
                  dataTraining.map((item, i) => {
                    return <p key={i}>{item}</p>;
                  })
                }
              </DataList>
            </ItemsContainer>
          </DataSection>
          <DataSection separator={props.separator | true} id={ProfileData[7].id}>
            <h2>{ProfileData[7].section}</h2>
            <p>{ProfileData[7].intro}</p>
            <ItemsContainer>
              {
                dataCertificate.map((item, i) => {
                  return (
                    <CertificateContainer key={i} bullet={props.bullet || 'military_tech'}>
                      <p>{item.course}</p>
                      <p>{item.platform}</p>
                    </CertificateContainer>
                  );
                })
              }
            </ItemsContainer>
          </DataSection>
          <DataSection separator={props.separator | true} id={ProfileData[8].id}>
            <h2>{ProfileData[8].section}</h2>
            <p>{ProfileData[8].intro}</p>
            <ItemsContainer>
              <DataList bullet={props.bullet || 'stream'}>
                {
                  dataMethodology.map((item, i) => {
                    return <p key={i}>{item}</p>;
                  })
                }
              </DataList>
            </ItemsContainer>
          </DataSection>
          <DataSection separator={props.separator | true} id={ProfileData[9].id}>
            <h2>{ProfileData[9].section}</h2>
            <p>{ProfileData[9].intro}</p>
            <ItemsContainer>
              {
                dataSkills.map((item, i) => {
                  return (
                    <DataItem key={i}>
                      <h3>{item.area}</h3>
                      <SkillsContainer bullet={props.bullet || 'bolt'} displaySub={props.displaySub | item.displaySub}>
                        {
                          item.subcontent.map((item, i) => {
                            return (
                              <div key={i}>
                                <p>{item.subhead}</p>
                                <div>
                                  {
                                    item.aptitudes.map((item, i) => {
                                      return (
                                        <span key={i}>{item}</span>
                                      );
                                    })
                                  }
                                </div>
                              </div>
                            );
                          })
                        }
                      </SkillsContainer>
                    </DataItem>
                  );
                })
              }
            </ItemsContainer>
          </DataSection>
          <DataSection separator={props.separator | true} id={ProfileData[10].id}>
            <h2>{ProfileData[10].section}</h2>
            <p>{ProfileData[10].intro}</p>
            <ItemsContainer>
              {
                dataTools.map((item, i) => {
                  return (
                    <DataItem key={i}>
                      <h3>{item.area}</h3>
                      <SkillsContainer bullet={props.bullet || 'subdirectory_arrow_right'}
                                       displaySub={props.displaySub | item.displaySub}>
                        {
                          item.subcontent.map((item, i) => {
                            return (
                              <div key={i}>
                                <p>{item.subhead}</p>
                                <div>
                                  {
                                    item.apps.map((item, i) => {
                                      return (
                                        <span key={i}>{item}</span>
                                      );
                                    })
                                  }
                                </div>
                              </div>
                            );
                          })
                        }
                      </SkillsContainer>
                    </DataItem>
                  );
                })
              }
            </ItemsContainer>
          </DataSection>
          <DataSection separator={props.separator | false} id={ProfileData[11].id}>
            <h2>{ProfileData[11].section}</h2>
            <ItemsContainer>
              {
                dataLanguage.map((item, i) => {
                  return (
                    <DataItem key={i}>
                      <h3>{item.language}</h3>
                      <p>{item.level}</p>
                    </DataItem>
                  );
                })
              }
            </ItemsContainer>
          </DataSection>
        </div>

        {/*Nav Sections*/}
        <div>
          <NavSectionsIcon className="material-icons"
                           onClick={() => setShowFloatSections(!showFloatSections)}>{showFloatSections ? 'close' : 'format_list_bulleted'}
          </NavSectionsIcon>
          <NavRightContainer floatSections={showFloatSections}>
            <div>
              {
                dataNavProfile.map((item, i) => {
                  return (
                    <NavItem
                      key={i}
                      ref={ref}
                      href={item.target}
                      active={props.active}
                      activeClassName={props.active}>{item.section}
                    </NavItem>
                  );
                })
              }
            </div>
          </NavRightContainer>
        </div>
      </DataContainer>
    </ContentWrapper>
  );
};

export default Profile;
